import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Stack,
  Card,
  CardContent,
  Avatar,
} from "@mui/material";
import {
  CorporateFareRounded,
  ChevronRightRounded,
  VisibilityRounded,
} from "@mui/icons-material";
import Table, { TableHeaderConfig } from "../components/Table";
import { api } from "../utils/api";
import FilterBar from "../components/FilterBar";
import dayjs from "dayjs";

interface Organization {
  id: string;
  name: string;
  patientCount: number;
  [key: string]: any;
}

interface Patient {
  id: string;
  name?: any[];
  gender?: string;
  [key: string]: any;
}

const OrgPage: React.FC = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const getPatientName = (row: any) => {
    const name = row.name?.[0];
    if (!name) return "N/A";
    return `${name?.given?.join(" ") || ""} ${name?.family || ""}`.trim();
  };

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await api.get("/pcc/patients-stored");
      const data = res.data;
      const rawData = Array.isArray(data) ? data : [];
      setPatients(rawData);
    } catch (err: any) {
      console.error("Error fetching data:", err);
      setError(err.message || "Failed to fetch data");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const organizations = useMemo(() => {
    const orgMap = new Map<string, Organization & { patientList: Patient[] }>();

    patients.forEach((item: any) => {
      const orgs = (item.organizations || item.details?.organizations || []) as any[];
      const patientData =
        item.patient ||
        item.details?.patient ||
        (item.resourceType === "Patient" ? item : null);

      if (!patientData) return;

      orgs.forEach((org: any) => {
        if (!org.id) return;

        if (!orgMap.has(org.id)) {
          orgMap.set(org.id, {
            id: org.id,
            name: org.name || `Org: ${org.id}`,
            patientCount: 0,
            patientList: [],
          });
        }

        const orgEntry = orgMap.get(org.id)!;
        const patientId = patientData.id || patientData._id;

        if (!orgEntry.patientList.find((p) => p.id === patientId)) {
          orgEntry.patientCount++;
          orgEntry.patientList.push({ ...patientData, id: patientId });
        }
      });
    });

    return Array.from(orgMap.values());
  }, [patients]);

  const filteredOrgs = useMemo(() => {
    return organizations.filter(
      (org) =>
        org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        org.id.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [organizations, searchQuery]);

  const selectedOrg = useMemo(() => {
    return organizations.find((o) => o.id === selectedOrgId);
  }, [organizations, selectedOrgId]);

  const orgHeaders: TableHeaderConfig[] = [
    {
      key: "name",
      label: "Organization Name",
      render: (row: any) => (
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ bgcolor: "primary.light", width: 32, height: 32 }}>
            <CorporateFareRounded sx={{ fontSize: 18 }} />
          </Avatar>
          <Typography
            variant="body2"
            fontWeight={700}
            onClick={() => setSelectedOrgId(row.id)}
            sx={{
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
              textDecoration: "underline",
            }}
          >
            {row.name}
          </Typography>
        </Stack>
      ),
    },
    {
      key: "id",
      label: "ID",
      render: (row: any) => (
        <Typography variant="caption" color="text.secondary" fontWeight={500}>
          {row.id}
        </Typography>
      ),
    },
    {
      key: "patients",
      label: "Patients",
      render: (row: any) => (
        <Typography variant="body2" fontWeight={600}>
          {row.patientCount} Patients
        </Typography>
      ),
    },
    {
      key: "actions",
      label: "",
      align: "right",
      render: (row: any) => (
        <Button
          variant="contained"
          size="small"
          onClick={() => setSelectedOrgId(row.id)}
          sx={{
            borderRadius: 1,
            textTransform: "none",
            fontWeight: 700,
            fontSize: "0.75rem",
          }}
        >
          View Patients
        </Button>
      ),
    },
  ];

  const patientHeaders: TableHeaderConfig[] = [
    {
      key: "nameLabel",
      label: "Patient Name",
      render: (row: any) => (
        <Box>
          <Typography
            variant="body2"
            fontWeight={700}
            onClick={() => navigate(`/patients/${row.id}`)}
            sx={{
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
              textDecoration: "underline",
            }}
          >
            {getPatientName(row)}
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={500}
            sx={{ fontSize: "12px" }}
          >
            <Box component="span" sx={{ fontWeight: 700 }}>
              Id:{" "}
            </Box>
            {row.id}
          </Typography>
        </Box>
      ),
    },
    {
      key: "telecom",
      label: "Contact",
      render: (row: any) => {
        const phones = (row.telecom || [])
          .filter((t: any) => t.system === "phone")
          .slice(0, 2);

        if (!phones.length) {
          return (
            <Typography variant="body2" fontWeight={500}>
              N/A
            </Typography>
          );
        }

        return (
          <Box>
            {phones.map((p: any, idx: number) => {
              const label = p.use
                ? p.use.charAt(0).toUpperCase() + p.use.slice(1)
                : "";

              return (
                <Typography
                  key={idx}
                  variant="caption"
                  display="block"
                  fontWeight={500}
                >
                  {label && (
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      {label}:{" "}
                    </Box>
                  )}
                  {p.value}
                </Typography>
              );
            })}
          </Box>
        );
      },
    },
    {
      key: "gender",
      label: "Gender",
      render: (row: any) => (
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{ textTransform: "capitalize" }}
        >
          {row.gender || "N/A"}
        </Typography>
      ),
    },
    {
      key: "birthDate",
      label: "Date of Birth",
      render: (row: any) => (
        <Typography variant="body2" fontWeight={500}>
          {row.birthDate ? dayjs(row.birthDate).format("DD MMM YYYY") : "N/A"}
        </Typography>
      ),
    },
    {
      key: "location",
      label: "City",
      render: (row: any) => {
        const addresses = (row.address || []).slice(0, 2);

        if (!addresses.length) {
          return (
            <Typography variant="body2" fontWeight={500}>
              N/A
            </Typography>
          );
        }

        return (
          <Box>
            {addresses.map((addr: any, idx: number) => {
              const label = addr.use
                ? addr.use.charAt(0).toUpperCase() + addr.use.slice(1)
                : "";

              return (
                <Typography
                  key={idx}
                  variant="caption"
                  display="block"
                  fontWeight={500}
                >
                  {label && (
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      {label}:{" "}
                    </Box>
                  )}
                  {addr.city || "N/A"}
                </Typography>
              );
            })}
          </Box>
        );
      },
    },
    {
      key: "actions",
      label: "Actions",
      align: "right",
      render: (row: any) => (
        <Button
          variant="outlined"
          size="small"
          startIcon={<VisibilityRounded sx={{ fontSize: "16px !important" }} />}
          onClick={() => navigate(`/patients/${row.id}`)}
          sx={{
            borderRadius: 1,
            textTransform: "none",
            fontWeight: 700,
            fontSize: "0.75rem",
            px: 2,
            py: 0.5,
            borderColor: "divider",
          }}
        >
          View
        </Button>
      ),
    },
  ];

  const patientGridTemplate = "2.5fr 1.5fr 0.7fr 1fr 1.5fr 0.7fr";

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => void fetchData()} sx={{ mt: 2 }}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Organizations
          </Typography>
          <Breadcrumbs
            separator={<ChevronRightRounded sx={{ fontSize: 16 }} />}
            sx={{ mt: 0.5 }}
          >
            <Link
              component="button"
              variant="caption"
              color={selectedOrgId ? "primary" : "text.secondary"}
              onClick={() => setSelectedOrgId(null)}
              sx={{
                underline: "none",
                fontWeight: 600,
                border: "none",
                background: "none",
                cursor: "pointer",
                p: 0,
              }}
            >
              All Organizations
            </Link>
            {selectedOrgId && (
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
              >
                {selectedOrg?.name}
              </Typography>
            )}
          </Breadcrumbs>
        </Box>
        {!selectedOrgId && (
          <FilterBar
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search organizations..."
            filterGroups={[]}
            chips={[]}
            onClearAll={() => setSearchQuery("")}
          />
        )}
      </Box>

      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 8,
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : selectedOrgId && selectedOrg ? (
        <Box>
          <Card
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              mb: 3,
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" spacing={3} alignItems="center">
                <Avatar
                  sx={{
                    bgcolor: "primary.main",
                    width: 56,
                    height: 56,
                    boxShadow: "0 4px 12px rgba(0,128,240,0.2)",
                  }}
                >
                  <CorporateFareRounded sx={{ fontSize: 32 }} />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    {selectedOrg.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Organization ID: {selectedOrg.id}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "inline-block",
                      mt: 0.5,
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      bgcolor: "primary.light",
                      color: "#fff",
                      fontWeight: 700,
                    }}
                  >
                    {selectedOrg.patientCount} Patients
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
          <Table
            headers={patientHeaders}
            data={selectedOrg.patientList}
            gridTemplateColumns={patientGridTemplate}
            emptyState="No patients found for this organization."
            variant="cards"
            title="Assigned Patients"
          />
        </Box>
      ) : (
        <Table
          headers={orgHeaders}
          data={filteredOrgs}
          gridTemplateColumns="2.5fr 1.5fr 1fr 0.5fr"
          emptyState="No organizations found."
          variant="cards"
          title=""
        />
      )}
    </Box>
  );
};

export default OrgPage;
